// Realistic Instagram feed-post preview card for generated content.
import React, { useState } from 'react';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ImageIcon, BadgeCheck } from 'lucide-react';
import { MTN_BRAND, MtnPost } from '@/lib/mtnshop';

export default function InstagramPostCard({ post }: { post: MtnPost }) {
  const [expanded, setExpanded] = useState(false);
  const [liked, setLiked] = useState(false);

  const caption = post.caption || '';
  const short = caption.length > 130 && !expanded ? caption.slice(0, 130) + '…' : caption;
  const likes = 214 + ((post.day_index + 3) * 37) % 400;
  const date = new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

  return (
    <div className="w-full max-w-[380px] rounded-2xl border border-white/10 bg-black overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.6)]">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3.5 py-2.5">
        <div className="rounded-full p-[2px] bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-500">
          <img src={MTN_BRAND.avatar} alt="MTN" className="h-8 w-8 rounded-full border-2 border-black object-cover" />
        </div>
        <div className="flex-1 leading-tight">
          <p className="text-[13px] font-semibold text-white flex items-center gap-1">
            {MTN_BRAND.handle}
            <BadgeCheck className="h-3.5 w-3.5 text-sky-400" />
          </p>
          <p className="text-[11px] text-zinc-500">{post.day_name} · Sponsored gear drop</p>
        </div>
        <MoreHorizontal className="h-5 w-5 text-zinc-400" />
      </div>

      {/* Image */}
      <div className="aspect-square w-full bg-zinc-900 relative">
        {post.image_url ? (
          <img src={post.image_url} alt={post.title || 'post'} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-zinc-600">
            <ImageIcon className="h-10 w-10" />
            <p className="text-xs px-8 text-center">{post.image_prompt ? 'Image not generated yet — prompt ready' : 'No image prompt'}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4 px-3.5 pt-3">
        <button onClick={() => setLiked(!liked)} aria-label="Like">
          <Heart className={`h-6 w-6 transition ${liked ? 'fill-red-500 text-red-500 scale-110' : 'text-white'}`} />
        </button>
        <MessageCircle className="h-6 w-6 text-white" />
        <Send className="h-6 w-6 text-white" />
        <Bookmark className="ml-auto h-6 w-6 text-white" />
      </div>

      {/* Meta */}
      <div className="px-3.5 pb-4 pt-2 space-y-1.5">
        <p className="text-[13px] font-semibold text-white">{(likes + (liked ? 1 : 0)).toLocaleString()} likes</p>
        <p className="text-[13px] text-zinc-200 whitespace-pre-line leading-snug">
          <span className="font-semibold text-white">{MTN_BRAND.handle}</span>{' '}
          {short}
          {caption.length > 130 && (
            <button onClick={() => setExpanded(!expanded)} className="ml-1 text-zinc-500">
              {expanded ? 'less' : 'more'}
            </button>
          )}
        </p>
        {post.hashtags && (
          <p className="text-[13px] leading-snug text-sky-400/90 break-words">{post.hashtags}</p>
        )}
        {post.cta && (
          <p className="text-[13px] text-zinc-300 font-medium">{post.cta}</p>
        )}
        <p className="text-[12px] text-zinc-500">
          View all {12 + (post.day_index * 5) % 30} comments
        </p>
        <div className="flex items-start gap-1.5">
          <p className="text-[12px] text-zinc-400">
            <span className="font-semibold text-zinc-300">rigger_dave</span> Exactly what our shop needed 🔥
          </p>
        </div>
        <p className="text-[10px] uppercase tracking-wide text-zinc-600">{date}</p>
      </div>
    </div>
  );
}
